import React from 'react';
import { Modal } from '@components/molecules/Modal';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { CollapsibleGroupHeader } from '@components/molecules/CollapsibleGroupHeader';
import Search from '@components/ui/Search';
import ReactSelect from '@components/atoms/ReactSelect';
import { Loading } from '@components/atoms/Loading';
import { Crew } from '@models/crew.model';
import { Task } from '@models/task.model';
import { formatDateOnly } from '@utils/formatDate';
import { useProjects } from '@hooks/api/projects/useProjects';
import { useAssignableTasks } from '@hooks/api/crew/useAssignableTasks';
import { useAssignTasksToCrew } from '@hooks/api/crew/useAssignTasksToCrew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

type Props = {
  crew: Crew | null;
  crewId: string;
  isOpen: boolean;
  onClose: () => void;
};

/** Sentinel values for the project dropdown, kept distinct from any real id. */
const FILTER_CREW_PROJECTS = '__crew_projects__';
const FILTER_ALL_PROJECTS = '__all_projects__';

/**
 * How a task reads in this modal. Assignment is the axis that matters here, so
 * it drives the badge — schedule status (upcoming, behind) is secondary and left
 * to the task detail views.
 *
 * Colours mirror `utils/taskDisplay.ts` so a task looks the same here as it does
 * everywhere else in the app.
 */
type Assignment =
  | { kind: 'unassigned' }
  | { kind: 'crew'; crewName: string }
  | { kind: 'subcontractor'; organizationName: string };

const BADGE_STYLES: Record<Assignment['kind'], string> = {
  unassigned: 'text-orange-700 bg-orange-50 border-orange-200',
  crew: 'text-lime-800 bg-lime-50 border-lime-200',
  subcontractor: 'text-blue-900 bg-blue-50 border-blue-200',
};

const DOT_STYLES: Record<Assignment['kind'], string> = {
  unassigned: 'bg-[#f77239]',
  crew: 'bg-[#7eb81c]',
  subcontractor: 'bg-[#254776]',
};

const BADGE_LABELS: Record<Assignment['kind'], string> = {
  unassigned: 'Unassigned',
  crew: 'Assigned to Crew',
  subcontractor: 'Assigned to Subcontractor',
};

/**
 * Crew names are stored however they were typed ("notify test"), and the crew
 * header prettifies them with the `capitalize` CSS class. These names also land
 * inside sentences here, where a CSS class can't reach — so do it in JS and use
 * the same value everywhere.
 */
const displayCrewName = (name?: string | null): string =>
  (name || 'this crew')
    .split(' ')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');

/** What holds this task right now, ignoring the crew being edited. */
const getAssignment = (task: Task, crewId: string): Assignment => {
  if (task.delegateOrganizationId) {
    return {
      kind: 'subcontractor',
      organizationName: task.delegateOrganization?.name || 'a subcontractor',
    };
  }

  const otherCrew = (task.crews || []).find((crew) => crew.crewId !== crewId);
  if (otherCrew) {
    return { kind: 'crew', crewName: displayCrewName(otherCrew.name) };
  }

  return { kind: 'unassigned' };
};

const dateRange = (task: Task): string => {
  const start = task.startTime ? formatDateOnly(task.startTime) : '';
  const end = task.endTime ? formatDateOnly(task.endTime) : '';
  if (!start && !end) return 'No dates set';
  return `${start || '—'} → ${end || '—'}`;
};

/** A task plus whether it renders indented under a parent shown right above it. */
type TaskEntry = { task: Task; nested: boolean };

/**
 * Puts every subtask straight after its parent so the column reads as the work
 * breakdown rather than a flat list.
 *
 * A subtask whose parent the current filter excluded is *not* indented — an
 * indent with nothing above it reads as a rendering glitch rather than as
 * hierarchy.
 */
const orderWithSubtasks = (tasks: Task[]): TaskEntry[] => {
  const presentIds = new Set(tasks.map((task) => task.taskId));
  const childrenByParent = new Map<string, Task[]>();
  const roots: Task[] = [];

  tasks.forEach((task) => {
    const parentId = task.parentTaskId;
    if (parentId && presentIds.has(parentId)) {
      childrenByParent.set(parentId, [
        ...(childrenByParent.get(parentId) || []),
        task,
      ]);
      return;
    }
    roots.push(task);
  });

  return roots.flatMap((root) => [
    { task: root, nested: false },
    ...(childrenByParent.get(root.taskId as string) || []).map((child) => ({
      task: child,
      nested: true,
    })),
  ]);
};

type ProjectGroup = {
  projectId: string;
  projectName: string;
  entries: TaskEntry[];
};

const groupByProject = (tasks: Task[]): ProjectGroup[] => {
  const groups = new Map<string, Task[]>();
  const names = new Map<string, string>();

  tasks.forEach((task) => {
    const projectId = task.projectId || 'unknown';
    groups.set(projectId, [...(groups.get(projectId) || []), task]);
    if (!names.has(projectId)) {
      names.set(projectId, task.project?.name || 'Unknown project');
    }
  });

  return Array.from(groups.entries())
    .map(([projectId, projectTasks]) => ({
      projectId,
      projectName: names.get(projectId) || 'Unknown project',
      entries: orderWithSubtasks(projectTasks),
    }))
    .sort((a, b) => a.projectName.localeCompare(b.projectName));
};

const TaskRow: React.FC<{
  task: Task;
  assignment: Assignment;
  direction: 'add' | 'remove';
  nested?: boolean;
  disabled?: boolean;
  takenFrom?: string;
  onClick: () => void;
}> = ({ task, assignment, direction, nested, disabled, takenFrom, onClick }) => {
  const locked = assignment.kind === 'subcontractor';
  const isMove = direction === 'add' && assignment.kind === 'crew';
  const isSubtask = Boolean(task.parentTaskId);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || locked}
      title={
        locked
          ? 'Delegated tasks are changed from the task Assignment tab'
          : direction === 'add'
            ? isMove
              ? 'Move to this crew'
              : 'Add to this crew'
            : 'Remove from this crew'
      }
      className={`group w-full flex items-start gap-3 px-3 py-2 text-left rounded-md transition-colors hover:bg-indigo-50 dark:hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent ${
        nested ? 'ml-4 pl-3 border-l-2 border-gray-200 dark:border-gray-600' : ''
      }`}>
      {direction === 'remove' && (
        <ArrowBackIcon
          className="mt-1.5 text-gray-300 group-hover:text-indigo-500 shrink-0"
          sx={{ fontSize: '18px' }}
        />
      )}

      <span
        className={`mt-2 w-2 h-2 rounded-full shrink-0 ${DOT_STYLES[assignment.kind]}`}
      />

      <div className="min-w-0 flex-1 flex flex-col gap-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
            {isSubtask ? '↳ ' : ''}
            {task.name}
          </span>
          <span
            className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide rounded border px-1.5 py-0.5 ${BADGE_STYLES[assignment.kind]}`}>
            {BADGE_LABELS[assignment.kind]}
          </span>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400 truncate m-0">
          {dateRange(task)}
          {task.manHours ? ` · ${task.manHours}h` : ''}
        </p>

        {/* Only meaningful while shopping on the left — in the right-hand column
            every row is this crew's by definition. */}
        {direction === 'add' && assignment.kind !== 'unassigned' && (
          <div className="flex flex-wrap items-center gap-1 mt-0.5">
            <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {assignment.kind === 'subcontractor' ? 'Subcontractor' : 'Currently'}
            </span>
            <span
              className={`max-w-full truncate text-[10px] font-medium rounded border px-1.5 py-0.5 ${BADGE_STYLES[assignment.kind]}`}>
              {assignment.kind === 'subcontractor'
                ? assignment.organizationName
                : assignment.crewName}
            </span>
          </div>
        )}

        {locked && (
          <p className="text-[10px] text-gray-500 dark:text-gray-400 italic m-0 mt-0.5">
            Change this from the task → Assignment tab.
          </p>
        )}

        {takenFrom && (
          <span className="mt-1 inline-flex items-center gap-1 self-start text-[10px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
            <WarningAmberIcon sx={{ fontSize: '12px' }} />
            Will be taken from {takenFrom}
          </span>
        )}
      </div>

      {direction === 'add' &&
        (locked ? (
          <LockOutlinedIcon
            className="mt-1.5 text-gray-300 shrink-0"
            sx={{ fontSize: '18px' }}
          />
        ) : isMove ? (
          <SwapHorizIcon
            className="mt-1.5 text-amber-500 shrink-0"
            sx={{ fontSize: '18px' }}
          />
        ) : (
          <ArrowForwardIcon
            className="mt-1.5 text-gray-300 group-hover:text-indigo-500 shrink-0"
            sx={{ fontSize: '18px' }}
          />
        ))}
    </button>
  );
};

const TaskColumn: React.FC<{
  title: string;
  count: number;
  groups: ProjectGroup[];
  emptyText: string;
  isLoading?: boolean;
  actionLabel: string;
  onAction: () => void;
  actionDisabled?: boolean;
  /**
   * While true every group renders open and the fold control disappears — a
   * search must never hide its own matches behind a fold. The collapsed set is
   * left untouched, so clearing the search restores the user's folds verbatim.
   */
  forceExpanded?: boolean;
  children: (entry: TaskEntry) => React.ReactNode;
}> = ({
  title,
  count,
  groups,
  emptyText,
  isLoading,
  actionLabel,
  onAction,
  actionDisabled,
  forceExpanded,
  children,
}) => {
  // Collapsed rather than expanded: an untouched project — including one that
  // only appears after a filter change — is open with no bookkeeping.
  const [collapsedProjectIds, setCollapsedProjectIds] = React.useState<
    Set<string>
  >(new Set());
  const columnId = React.useId();

  const toggleGroup = (projectId: string) =>
    setCollapsedProjectIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(projectId)) next.add(projectId);
      return next;
    });

  return (
    <div className="flex flex-col border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden min-w-0">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-200 truncate">
          {title} ({count})
        </span>
        <button
          type="button"
          onClick={onAction}
          disabled={actionDisabled}
          className="shrink-0 text-xs font-medium text-indigo-600 dark:text-indigo-300 hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed">
          {actionLabel}
        </button>
      </div>

      <div className="h-96 overflow-y-auto p-1">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <Loading size="lg" />
          </div>
        ) : groups.length === 0 ? (
          <p className="h-full flex items-center justify-center text-center text-xs text-gray-500 dark:text-gray-400 px-4 m-0">
            {emptyText}
          </p>
        ) : (
          groups.map((group) => {
            const expanded =
              Boolean(forceExpanded) ||
              !collapsedProjectIds.has(group.projectId);
            // Both columns render the same project ids, so the id needs a
            // per-column prefix or `aria-controls` would point at the list in
            // the other column.
            const bodyId = `${columnId}-${group.projectId}`;

            return (
              <div key={group.projectId} className="mb-2">
                <CollapsibleGroupHeader
                  title={group.projectName}
                  count={group.entries.length}
                  expanded={expanded}
                  controlsId={bodyId}
                  onToggle={
                    forceExpanded
                      ? undefined
                      : () => toggleGroup(group.projectId)
                  }
                />
                {/* Rendered even when folded so `aria-controls` resolves; only
                    the rows themselves unmount. Keep it class-free — padding
                    here would break TaskRow's subtask indent. */}
                <div id={bodyId}>
                  {expanded && group.entries.map((entry) => children(entry))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export const ManageCrewTasksModal: React.FC<Props> = ({
  crew,
  crewId,
  isOpen,
  onClose,
}) => {
  const { data: projects } = useProjects();
  const { mutateAsync: assignTasks, isPending: isSaving } =
    useAssignTasksToCrew();

  const [search, setSearch] = React.useState('');
  const [includeCompleted, setIncludeCompleted] = React.useState(false);
  const [showOtherCrewTasks, setShowOtherCrewTasks] = React.useState(true);
  const [projectFilter, setProjectFilter] = React.useState<string>(
    FILTER_CREW_PROJECTS,
  );
  const [pendingMove, setPendingMove] = React.useState<Task | null>(null);
  const [skipMoveConfirm, setSkipMoveConfirm] = React.useState(false);

  const crewLabel = displayCrewName(crew?.name);
  const crewTasks = React.useMemo(() => crew?.tasks || [], [crew?.tasks]);

  // Key on the assignment *set*, not on array identity: a background refetch of
  // the crews query hands back new objects every time, and resetting off that
  // would wipe the user's in-progress edits.
  const crewTaskIdsKey = crewTasks
    .map((task) => task.taskId)
    .filter((id): id is string => Boolean(id))
    .sort()
    .join(',');
  const crewTaskIds = React.useMemo(
    () => (crewTaskIdsKey ? crewTaskIdsKey.split(',') : []),
    [crewTaskIdsKey],
  );

  /** The crew's *desired* task set — what Save will persist. */
  const [selectedIds, setSelectedIds] = React.useState<string[]>(crewTaskIds);
  React.useEffect(() => setSelectedIds(crewTaskIds), [crewTaskIds]);

  const crewProjectIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          crewTasks
            .map((task) => task.projectId)
            .filter((id): id is string => Boolean(id)),
        ),
      ).sort(),
    [crewTasks],
  );

  // A crew with no work yet has no projects of its own, so that filter would
  // show an empty column. Fall back to the whole organization instead.
  const requestedProjectIds = React.useMemo(() => {
    if (projectFilter === FILTER_ALL_PROJECTS) return undefined;
    if (projectFilter === FILTER_CREW_PROJECTS) {
      return crewProjectIds.length > 0 ? crewProjectIds : undefined;
    }
    return [projectFilter];
  }, [projectFilter, crewProjectIds]);

  const { data: assignableTasks, isLoading } = useAssignableTasks({
    projectIds: requestedProjectIds,
    includeCompleted,
    enabled: isOpen,
  });

  // Merged so the right-hand column can still render a task the current filter
  // excluded — the crew keeps it either way.
  const taskIndex = React.useMemo(() => {
    const index = new Map<string, Task>();
    crewTasks.forEach((task) => {
      if (task.taskId) index.set(task.taskId, task);
    });
    (assignableTasks || []).forEach((task) => {
      if (task.taskId) index.set(task.taskId, task);
    });
    return index;
  }, [crewTasks, assignableTasks]);

  const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);
  const originalSet = React.useMemo(() => new Set(crewTaskIds), [crewTaskIds]);

  const term = search.trim().toLowerCase();
  const matchesSearch = (task: Task) =>
    !term || (task.name || '').toLowerCase().includes(term);

  const availableTasks = (assignableTasks || []).filter((task) => {
    if (!task.taskId || selectedSet.has(task.taskId)) return false;
    if (!matchesSearch(task)) return false;
    if (!showOtherCrewTasks) {
      return getAssignment(task, crewId).kind !== 'crew';
    }
    return true;
  });

  const selectedTasks = selectedIds
    .map((taskId) => taskIndex.get(taskId))
    .filter((task): task is Task => Boolean(task))
    .filter(matchesSearch);

  const availableGroups = groupByProject(availableTasks);
  const selectedGroups = groupByProject(selectedTasks);

  /** Tasks staged to be taken from another crew, keyed by task id. */
  const takenFromByTask = React.useMemo(() => {
    const map = new Map<string, string>();
    selectedIds.forEach((taskId) => {
      if (originalSet.has(taskId)) return;
      const task = taskIndex.get(taskId);
      if (!task) return;
      const assignment = getAssignment(task, crewId);
      if (assignment.kind === 'crew') map.set(taskId, assignment.crewName);
    });
    return map;
  }, [selectedIds, originalSet, taskIndex, crewId]);

  const addedCount = selectedIds.filter((id) => !originalSet.has(id)).length;
  const removedCount = crewTaskIds.filter((id) => !selectedSet.has(id)).length;
  const hasChanges = addedCount > 0 || removedCount > 0;

  const addTask = (taskId: string) =>
    setSelectedIds((prev) => (prev.includes(taskId) ? prev : [...prev, taskId]));
  const removeTask = (taskId: string) =>
    setSelectedIds((prev) => prev.filter((id) => id !== taskId));

  const handleAddClick = (task: Task) => {
    if (!task.taskId) return;
    const assignment = getAssignment(task, crewId);
    if (assignment.kind === 'subcontractor') return;

    // Only one crew can hold a task, so taking it costs another crew its work.
    // That deserves a confirmation the first time, not on every click.
    if (assignment.kind === 'crew' && !skipMoveConfirm) {
      setPendingMove(task);
      return;
    }

    addTask(task.taskId);
  };

  const confirmPendingMove = () => {
    if (pendingMove?.taskId) addTask(pendingMove.taskId);
    setPendingMove(null);
  };

  const projectOptions = React.useMemo(
    () => [
      { value: FILTER_CREW_PROJECTS, label: "This crew's projects" },
      { value: FILTER_ALL_PROJECTS, label: 'All projects' },
      ...(projects || [])
        .filter((project: any) => project?.projectId)
        .map((project: any) => ({
          value: project.projectId as string,
          label: project.name || 'Untitled project',
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label)),
    ],
    [projects],
  );

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleSave = async () => {
    if (!crewId || isSaving) return;
    try {
      await assignTasks({ crewId, taskIds: selectedIds });
      onClose();
    } catch {
      // The mutation hook surfaces the failure via toast; keep the modal open so
      // the user's staged changes aren't lost.
    }
  };

  const pendingMoveCrew = pendingMove
    ? getAssignment(pendingMove, crewId)
    : null;

  return (
    <>
      <Modal
        title="Manage Crew Tasks"
        description="Click a task to move it between lists. Each task can only be worked by one crew."
        isOpen={isOpen}
        onClose={handleClose}
        onAfterClose={() => {
          setSelectedIds(crewTaskIds);
          setSearch('');
          setProjectFilter(FILTER_CREW_PROJECTS);
          setIncludeCompleted(false);
          setShowOtherCrewTasks(true);
          setSkipMoveConfirm(false);
          setPendingMove(null);
        }}
        isLoading={isSaving}
        confirmText={
          addedCount + removedCount > 0
            ? `Save Changes (${addedCount + removedCount})`
            : 'Save Changes'
        }
        handleConfirm={handleSave}
        disableConfirm={isSaving || !hasChanges}
        closeOnOverlayClick={!isSaving}
        closeOnEscape={!isSaving}
        size="large">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ReactSelect
              label="Project"
              options={projectOptions}
              value={projectOptions.find(
                (option) => option.value === projectFilter,
              )}
              isClearable={false}
              isDisabled={isSaving}
              onChange={(option: any) =>
                setProjectFilter(option?.value || FILTER_CREW_PROJECTS)
              }
            />
            <div className="flex flex-col justify-end">
              <Search
                placeholder="Search tasks by name..."
                value={search}
                disabled={isSaving}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600 dark:text-gray-300">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                className="rounded"
                checked={includeCompleted}
                disabled={isSaving}
                onChange={(event) => setIncludeCompleted(event.target.checked)}
              />
              Show completed / canceled
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                className="rounded"
                checked={showOtherCrewTasks}
                disabled={isSaving}
                onChange={(event) =>
                  setShowOtherCrewTasks(event.target.checked)
                }
              />
              Show tasks assigned to other crews
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TaskColumn
              title="Available"
              count={availableTasks.length}
              groups={availableGroups}
              isLoading={isLoading}
              forceExpanded={Boolean(term)}
              actionLabel="Add all"
              actionDisabled={isSaving || availableTasks.length === 0}
              onAction={() =>
                setSelectedIds((prev) => [
                  ...prev,
                  ...availableTasks
                    .filter(
                      (task) =>
                        getAssignment(task, crewId).kind !== 'subcontractor',
                    )
                    .map((task) => task.taskId as string),
                ])
              }
              emptyText={
                term
                  ? 'No matches.'
                  : 'No tasks available for this filter. Try another project.'
              }>
              {({ task, nested }) => (
                <TaskRow
                  key={task.taskId}
                  task={task}
                  nested={nested}
                  assignment={getAssignment(task, crewId)}
                  direction="add"
                  disabled={isSaving}
                  onClick={() => handleAddClick(task)}
                />
              )}
            </TaskColumn>

            <TaskColumn
              title={`${crewLabel}'s tasks`}
              count={selectedIds.length}
              groups={selectedGroups}
              forceExpanded={Boolean(term)}
              actionLabel="Remove all"
              actionDisabled={isSaving || selectedTasks.length === 0}
              onAction={() => {
                const visible = new Set(
                  selectedTasks.map((task) => task.taskId),
                );
                setSelectedIds((prev) => prev.filter((id) => !visible.has(id)));
              }}
              emptyText={
                term
                  ? 'No matches.'
                  : 'No tasks yet — add work from the left.'
              }>
              {({ task, nested }) => (
                <TaskRow
                  key={task.taskId}
                  task={task}
                  nested={nested}
                  assignment={{
                    kind: 'crew',
                    crewName: crewLabel,
                  }}
                  direction="remove"
                  disabled={isSaving}
                  takenFrom={takenFromByTask.get(task.taskId as string)}
                  onClick={() => removeTask(task.taskId as string)}
                />
              )}
            </TaskColumn>
          </div>

          {hasChanges && (
            <p className="text-xs text-gray-500 dark:text-gray-400 m-0">
              {addedCount > 0 && (
                <span className="text-emerald-700 font-medium">
                  {addedCount} {addedCount === 1 ? 'task' : 'tasks'} to add
                </span>
              )}
              {addedCount > 0 && removedCount > 0 && ' · '}
              {removedCount > 0 && (
                <span className="text-gray-700 dark:text-gray-200 font-medium">
                  {removedCount} to remove
                </span>
              )}
              {takenFromByTask.size > 0 && (
                <span className="text-amber-700 font-medium">
                  {' · '}
                  {takenFromByTask.size} taken from{' '}
                  {Array.from(new Set(takenFromByTask.values())).join(', ')}
                </span>
              )}
              <span> · nothing is written until you save</span>
            </p>
          )}
        </div>
      </Modal>

      <ConfirmationModal
        variant="warning"
        isOpen={Boolean(pendingMove)}
        title={`Move this task to ${crewLabel}?`}
        description={
          pendingMoveCrew?.kind === 'crew'
            ? `"${pendingMove?.name}" is currently assigned to ${pendingMoveCrew.crewName}. A task can only have one crew, so ${pendingMoveCrew.crewName} will lose it.`
            : ''
        }
        confirmText={`Move to ${crewLabel}`}
        onConfirm={confirmPendingMove}
        onClose={() => setPendingMove(null)}>
        <label className="mt-3 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            className="rounded"
            checked={skipMoveConfirm}
            onChange={(event) => setSkipMoveConfirm(event.target.checked)}
          />
          Don&apos;t ask again for this session
        </label>
      </ConfirmationModal>
    </>
  );
};

export default ManageCrewTasksModal;
